import dayjs from "dayjs";
import { useState, useEffect } from "react";

export const formatTime = (timeStr) => {
  if (!timeStr) return "--";
  const [hour, minute] = timeStr.split(":");
  const h = parseInt(hour);
  const ampm = h >= 12 ? "PM" : "AM";
  const formattedHour = (((h + 11) % 12) + 1).toString().padStart(2, "0");
  return `${formattedHour}:${minute} ${ampm}`;
};

export const getLateTime = (checkIn) => {
  if (!checkIn) return "--";
  const [h, m] = checkIn.split(":").map(Number);
  const scheduledHour = 9;
  const scheduledMin = 0;

  const diffMins = h * 60 + m - (scheduledHour * 60 + scheduledMin);
  return diffMins > 0 ? `${diffMins} Min` : "On Time";
};

export const formatDecimalHours = (decimalHours) => {
  if (decimalHours == 0 || null) {
    return 0.0 + " m";
  }
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours} hr ${minutes} m`;
};

export const useFormattedDateTime = () => {
  const formatDateTime = () => {
    const now = new Date();

    const time = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const date = now.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return `${time}, ${date}`;
  };

  const [dateTime, setDateTime] = useState(formatDateTime());

  useEffect(() => {
    const interval = setInterval(() => {
      setDateTime(formatDateTime());
    }, 1000); // updates every second

    return () => clearInterval(interval); // cleanup
  }, []);

  return dateTime;
};

// In JSX

export const getProductionHours = (checkInTime) => {
  if (!checkInTime) return "0 min";

  // Get today's date in YYYY-MM-DD
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const todayStr = `${yyyy}-${mm}-${dd}`;

  // Combine date with time
  const fullDateTimeStr = `${todayStr}T${checkInTime}`;

  const checkIn = new Date(fullDateTimeStr);
  if (isNaN(checkIn.getTime())) {
    console.error("Invalid datetime:", fullDateTimeStr);
    return "0 min";
  }

  const now = new Date();
  const diffMs = now.getTime() - checkIn.getTime();

  if (diffMs < 0) return "0 min"; // future check-in time

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0) result += `${minutes} min`;

  return result.trim() || "0 min";
};

export const formatTimeCheckinOut = (timeStr) => {
  return timeStr && dayjs(timeStr, "HH:mm:ss").isValid()
    ? dayjs(timeStr, "HH:mm:ss").format("hh:mm A")
    : "N/A";
};

export const isAttendanceEditable = (record) => {
  const recordDate = dayjs(record?.Date || record?.date);
  if (!recordDate.isValid()) {
    return { editable: false, reason: "Invalid date" };
  }

  const today = dayjs().startOf("day");
  const threeDaysAgo = today.subtract(3, "day");

  if (recordDate.isAfter(today, "day")) {
    return {
      editable: false,
      reason: "Future records cannot be edited",
    };
  }

  if (recordDate.isBefore(threeDaysAgo, "day")) {
    return {
      editable: false,
      reason: "Only records from the last 3 days can be edited",
    };
  }

  if (recordDate.day() === 0) {
    return { editable: false, reason: "Sunday records cannot be edited" };
  }

  const status = record?.Status || record?.status;
  if (status === "Holiday") {
    return { editable: false, reason: "Holiday records cannot be edited" };
  }
  if (record?.holidayTitle) {
    return { editable: false, reason: "Holiday records cannot be edited" };
  }

  if (status === "Leave" || record?.isLeaveDay) {
    return { editable: false, reason: "Leave day records cannot be edited" };
  }

  if (status === "Week Off") {
    return { editable: false, reason: "Week off records cannot be edited" };
  }

  // Allow "Absent" status to be editable
  if (status === "Absent") {
    // If it's Absent, we allow editing to create a manual record
    return { editable: true, reason: null };
  }

  return { editable: true, reason: null };
};
